import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import type { Partner, StockLocation } from '../../types/stock';

export function PartnerInventoryPortal({ businessId }: { businessId: string }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'THIRD_PARTY_3PL' | 'RETAIL_CONSIGNMENT'>('RETAIL_CONSIGNMENT');
  const [sku, setSku] = useState('');
  const [qty, setQty] = useState(1);
  const [locationId, setLocationId] = useState('');
  const [type, setType] = useState<'sale' | 'receipt' | 'return' | 'cycle_count'>('sale');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubs = [
      onSnapshot(query(collection(db, 'partners'), where('businessId', '==', businessId)), (snap) =>
        setPartners(snap.docs.map((d) => d.data() as Partner)),
      ),
      onSnapshot(query(collection(db, 'stock_locations'), where('businessId', '==', businessId)), (snap) =>
        setLocations(snap.docs.map((d) => d.data() as StockLocation)),
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [businessId]);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'createPartner');
      await fn({ businessId, name: name.trim(), kind, locationIds: locations.map((l) => l.locationId) });
      setName('');
      setMessage('Partner created');
    } catch (err: any) {
      setMessage(err?.message || 'Failed');
    }
    setBusy(false);
  };

  const mintKey = async (partnerId: string) => {
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'createPartnerApiKey');
      const res: any = await fn({ businessId, partnerId, label: 'Portal key' });
      setMessage(`API key (copy now): ${res.data.apiKey}`);
    } catch (err: any) {
      setMessage(err?.message || 'Failed');
    }
    setBusy(false);
  };

  const report = async () => {
    if (!sku || !locationId) return;
    setBusy(true);
    try {
      const record = httpsCallable(functions, 'recordPartnerMovement');
      const res: any = await record({ businessId, locationId, sku: sku.trim(), quantity: qty, type });
      const { sizeLabel, before, after } = res.data;
      setMessage(
        after === null || before === null
          ? 'Entry recorded'
          : `${sku.trim()} (${sizeLabel}): ${before} → ${after}`,
      );
      setSku('');
    } catch (err: any) {
      setMessage(err?.message || 'Could not record that entry');
    }
    setBusy(false);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div>
          <p className="text-sm font-medium text-heading">Warehouse & retail partners</p>
          <p className="text-xs text-body">
            Integrated partners POST to <code>/partnerInventoryEvent</code> with an API key and
            Idempotency-Key. Manual stores can still be added here.
          </p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Partner name" value={name} onChange={(e) => setName(e.target.value)} />
          <select className="text-sm border rounded-md px-2" value={kind} onChange={(e) => setKind(e.target.value as any)}>
            <option value="RETAIL_CONSIGNMENT">Retail</option>
            <option value="THIRD_PARTY_3PL">3PL</option>
          </select>
          <Button type="button" size="sm" disabled={busy} onClick={create}>Add partner</Button>
        </div>
        {partners.map((p) => (
          <div key={p.partnerId} className="flex items-center justify-between text-sm border rounded-lg px-3 py-2">
            <span>{p.name} · {p.kind === 'THIRD_PARTY_3PL' ? '3PL' : 'Retail'}</span>
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => mintKey(p.partnerId)}>
              New API key
            </Button>
          </div>
        ))}
        <div className="border-t pt-3 space-y-2">
          <p className="text-xs font-medium text-heading uppercase tracking-wider">Manual sale / receipt</p>
          <p className="text-[11px] text-body">
            Enter the SKU exactly as it appears in your catalog. A count sets the location's total;
            a sale or receipt adjusts it.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="SKU" value={sku} onChange={(e) => setSku(e.target.value)} />
            <Input type="number" min={0} value={qty} onChange={(e) => setQty(Number(e.target.value))} />
            <select className="text-sm border rounded-md h-9 px-2" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Location…</option>
              {locations.map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
            </select>
            <select className="text-sm border rounded-md h-9 px-2" value={type} onChange={(e) => setType(e.target.value as any)}>
              <option value="sale">Sale</option>
              <option value="receipt">Receipt</option>
              <option value="return">Return</option>
              <option value="cycle_count">Count</option>
            </select>
          </div>
          <Button type="button" size="sm" disabled={busy || !sku.trim() || !locationId} onClick={report}>
            Record entry
          </Button>
        </div>
        {message && <p className="text-xs text-body break-all">{message}</p>}
      </CardContent>
    </Card>
  );
}
